import React, { useState } from 'react'

import * as Dialog from '@radix-ui/react-dialog'
import { Hash, X } from 'lucide-react'

import { useFocusReturn } from 'Hooks/useFocusReturn'
import { noty } from 'Utils'

import { ConfirmAction, DangerButton } from '../../../../components/ConfirmAction'
import { STATUS_COLOR_VAR, STATUS_LABEL } from '../../StatusBage'
import { Order } from '../../types'
import {
  deriveOrderStatus,
  getDateLabel,
  getDisplayId,
  getEstimatedDeliveryLabel,
  getScheduledLabel,
} from '../../utils'
import { OrderSummary } from '../OrderSummary'

interface OrderDetailsDialogProps {
  onOpenChange: (open: boolean) => void
  open: boolean
  order: Order
}

const cancelOrder = (orderId: string): void => {
  console.log('[stub] cancelOrder', orderId)
}

const reorderOrder = (orderId: string): void => {
  console.log('[stub] reorderOrder', orderId)
}

export const OrderDetailsDialog: React.FC<OrderDetailsDialogProps> = ({ onOpenChange, open, order }) => {
  const [confirmCancel, setConfirmCancel] = useState(false)
  const cancelTriggerRef = useFocusReturn<HTMLButtonElement>(confirmCancel)

  const status = deriveOrderStatus(order)
  const displayId = getDisplayId(order)
  const statusLabel = STATUS_LABEL[status]
  const statusColor = `var(${STATUS_COLOR_VAR[status]})`
  const trackingUrl = order.externalDelivery?.trackingUrl
  const scheduledLabel = getScheduledLabel(order) || getDateLabel(order, status)
  const estimatedDeliveryLabel = getEstimatedDeliveryLabel(order)
  const canCancel = status === 'pending' || status === 'in_progress'
  const canReorder = status === 'completed'

  // Land back on a clean confirm state each time the dialog is closed —
  // handled inline so we don't setState from an effect body.
  const handleOpenChange = (next: boolean) => {
    if (!next) setConfirmCancel(false)
    onOpenChange(next)
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="user-profile-order-dialog__overlay" />
        <Dialog.Content
          className="user-profile-order-dialog__content"
          style={{ '--status-color': statusColor } as React.CSSProperties}
        >
          <div className="user-profile-order-dialog__header">
            <Dialog.Title className="user-profile-order-dialog__title">
              <Hash aria-hidden size={18} strokeWidth={2} />
              {displayId}
            </Dialog.Title>
            <Dialog.Description className="sr-only">
              Order {displayId} details and actions, status {statusLabel}
            </Dialog.Description>
            <span className="user-profile-order-dialog__status">{statusLabel}</span>
            <Dialog.Close asChild>
              <button aria-label="Close" className="user-profile-order-dialog__close" type="button">
                <X aria-hidden size={18} strokeWidth={1.75} />
              </button>
            </Dialog.Close>
          </div>

          <div className="user-profile-order-dialog__body">
            {scheduledLabel ? <p className="user-profile-order-dialog__schedule">{scheduledLabel}</p> : null}
            {estimatedDeliveryLabel ? (
              <p className="user-profile-order-dialog__schedule">Estimated delivery: {estimatedDeliveryLabel}</p>
            ) : null}

            <OrderSummary order={order} />
          </div>

          <div className="user-profile-order-dialog__footer">
            {confirmCancel ? (
              <ConfirmAction
                confirmLabel="Cancel order"
                prompt="Cancel this order?"
                onCancel={() => setConfirmCancel(false)}
                onConfirm={() => {
                  cancelOrder(order.id ?? '')
                  noty('Cancellation isn’t available here yet.', 'info')
                  setConfirmCancel(false)
                  onOpenChange(false)
                }}
              />
            ) : (
              <>
                {trackingUrl ? (
                  <button
                    className="user-profile-link-button"
                    type="button"
                    onClick={() => window.open(trackingUrl, '_blank', 'noopener,noreferrer')}
                  >
                    Track
                  </button>
                ) : null}
                {canReorder ? (
                  <button
                    className="user-profile-link-button"
                    type="button"
                    onClick={() => {
                      reorderOrder(order.id ?? '')
                      noty('Reorder isn’t available here yet.', 'info')
                    }}
                  >
                    Reorder
                  </button>
                ) : null}
                {canCancel ? (
                  <DangerButton ref={cancelTriggerRef} icon="cancel" onClick={() => setConfirmCancel(true)}>
                    Cancel order
                  </DangerButton>
                ) : null}
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
